import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX } = FiIcons;

const LegalDisclaimerModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center overflow-y-auto py-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          >
            {/* Modal */}
            <motion.div
              className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 my-8"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">ClipShare Legal Disclaimer</h3>
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
              
              {/* Content */}
              <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
                <div className="text-sm text-gray-600 space-y-6">
                  <div className="flex justify-between text-gray-500 text-sm mb-6">
                    <div>Effective Date: {currentDate}</div>
                    <div>Last Updated: {currentDate}</div>
                  </div>

                  <section>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">1. Acceptance of Terms</h4>
                    <p className="mb-4">
                      By accessing or using ClipShare (the "Service"), you acknowledge that you have read, understood, and agree to be bound by this Legal Disclaimer and our accompanying Privacy Policy. If you do not agree with any part of these terms, you must not use the Service.
                    </p>
                  </section>

                  <section>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">2. Service Description</h4>
                    <p className="mb-4">
                      ClipShare is a web-based clipboard-sharing platform that allows users to exchange text content in real time through password-protected rooms. The Service is provided "as is" and "as available," without warranties of any kind.
                    </p>
                  </section>

                  <section>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">3. User Responsibilities</h4>
                    <h5 className="text-md font-medium text-gray-700 mb-1">3.1 Appropriate Use</h5>
                    <p className="mb-3">
                      You agree to use ClipShare only for lawful purposes and in compliance with all applicable laws and regulations. You shall not:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-1">
                      <li>Upload or share content that is illegal, defamatory, obscene, or infringes the rights of others.</li>
                      <li>Attempt to gain unauthorized access to other rooms or ClipShare's infrastructure.</li>
                      <li>Introduce malware, ransomware, or other malicious code.</li>
                      <li>Use the Service to transmit unsolicited advertising (spam).</li>
                      <li>Interfere with or disrupt the integrity or performance of the Service.</li>
                    </ul>

                    <h5 className="text-md font-medium text-gray-700 mb-1">3.2 Content Responsibility</h5>
                    <p className="mb-3">
                      You are solely responsible for all content you share. ClipShare does not pre-screen or actively monitor user content and assumes no liability for material posted by users. You represent and warrant that you own or have the right to share all content you upload.
                    </p>

                    <h5 className="text-md font-medium text-gray-700 mb-1">3.3 Security Practices</h5>
                    <ul className="list-disc pl-6 mb-4 space-y-1">
                      <li>Keep room passwords confidential.</li>
                      <li>Do not share highly sensitive personal or financial information.</li>
                      <li>Log out or leave rooms when finished.</li>
                      <li>Ensure your devices are secure and up to date.</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">4. Intellectual Property</h4>
                    <h5 className="text-md font-medium text-gray-700 mb-1">4.1 Your Content</h5>
                    <p className="mb-3">
                      You retain all ownership rights to content you share. By using the Service, you grant ClipShare a limited, non-exclusive license to transmit, display, and store your content solely to operate the Service. This license terminates automatically when your content is deleted from our systems.
                    </p>

                    <h5 className="text-md font-medium text-gray-700 mb-1">4.2 ClipShare Property</h5>
                    <p className="mb-3">
                      All software, design elements, and branding associated with ClipShare—including logos, name, and underlying code—are the exclusive property of ClipShare or its licensors. You may not copy, modify, distribute, reverse-engineer, or create derivative works from any part of the Service without prior written consent.
                    </p>
                  </section>

                  <section>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">5. Liability Limitations</h4>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                      <li><strong>No Warranties</strong> – ClipShare disclaims all express or implied warranties, including merchantability, fitness for a particular purpose, and non-infringement.</li>
                      <li><strong>Service Availability</strong> – We do not guarantee continuous, uninterrupted, or error-free operation. The Service may be unavailable due to maintenance, updates, or technical failures.</li>
                      <li><strong>Data Security</strong> – While we employ encryption and best-practice security measures, no internet transmission is 100% secure. You assume all risk for content you choose to share.</li>
                      <li><strong>Indirect Damages</strong> – To the fullest extent permitted by law, ClipShare is not liable for any indirect, incidental, consequential, exemplary, or punitive damages, including lost profits, data loss, or business interruption arising out of or in connection with the Service.</li>
                      <li><strong>Aggregate Liability</strong> – ClipShare's total cumulative liability to you for any claim or series of related claims will not exceed USD 100 or the amount you paid (if any) for using the Service during the twelve months preceding the claim, whichever is greater.</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">6. Indemnification</h4>
                    <p className="mb-3">
                      You agree to defend, indemnify, and hold harmless ClipShare, its officers, directors, employees, and affiliates from and against any claims, damages, liabilities, losses, and expenses (including reasonable attorneys' fees) arising out of or related to:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-1">
                      <li>Your use or misuse of the Service.</li>
                      <li>Any content you upload, share, or transmit through the Service.</li>
                      <li>Your violation of this Legal Disclaimer or any applicable law.</li>
                      <li>Your infringement of any intellectual-property or privacy rights of another person or entity.</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">7. Modifications to the Service</h4>
                    <p className="mb-4">
                      ClipShare reserves the right to modify, suspend, or discontinue any aspect of the Service at any time without notice. We are not liable to you or any third party for any modification, suspension, or discontinuation.
                    </p>
                  </section>

                  <section>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">8. Governing Law and Jurisdiction</h4>
                    <p className="mb-4">
                      This Disclaimer shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict-of-law principles. Any dispute arising under or in connection with the Service shall be brought exclusively in the courts located in the United States, and you consent to the personal jurisdiction of such courts.
                    </p>
                  </section>

                  <section>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">9. Severability</h4>
                    <p className="mb-4">
                      If any provision of this Disclaimer is found unenforceable by a court of competent jurisdiction, that provision will be limited or eliminated to the minimum extent necessary, and the remaining provisions will remain in full force and effect.
                    </p>
                  </section>

                  <section>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">10. Entire Agreement</h4>
                    <p className="mb-4">
                      This Legal Disclaimer, together with our Privacy Policy, constitutes the entire agreement between you and ClipShare regarding your use of the Service and supersedes all prior or contemporaneous understandings or agreements.
                    </p>
                  </section>
                </div>
              </div>
              
              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default LegalDisclaimerModal;